import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setPaymentStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.paymentStatus === 'paid') {
          setPaymentStatus('success');
          setPaymentDetails(response.data);
        } else if (response.data.status === 'expired') {
          setPaymentStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setPaymentStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setPaymentStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-16 w-16 animate-spin text-sky-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment...</h2>
            <p className="text-slate-600">Please wait while we verify your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50">
        <Card className="max-w-md w-full mx-4 border-emerald-200">
          <CardContent className="pt-6 text-center">
            <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your payment. Your booking has been confirmed.
            </p>
            {paymentDetails && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-semibold">Amount:</span> £{paymentDetails.amount?.toFixed(2)}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Quote ID:</span> {paymentDetails.quoteId}
                </p>
              </div>
            )}
            <p className="text-sm text-slate-600 mb-6">
              We'll contact you shortly to schedule your cleaning service.
            </p>
            <Link to="/">
              <Button className="w-full bg-sky-600 hover:bg-sky-700">
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50">
      <Card className="max-w-md w-full mx-4 border-red-200">
        <CardContent className="pt-6 text-center">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Verification Issue</h2>
          <p className="text-slate-600 mb-6">
            We're having trouble verifying your payment. Please check your email for confirmation or contact us directly.
          </p>
          <div className="space-y-3">
            <Link to="/contact">
              <Button className="w-full bg-sky-600 hover:bg-sky-700">
                Contact Us
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Return to Home
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;